import { LabelWrapper } from '@/modules/global-components/ui/wrappers/LabelWrapper';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import type { PopupProps } from '@/types/component';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useRef, useState } from 'react';
import Cropper, { type Point, type Area } from 'react-easy-crop';
import { Controller, useForm } from 'react-hook-form';
import { TbBuilding, TbShieldCheck, TbUserCircle } from 'react-icons/tb';
import {
  updateProfileInitialState,
  updateProfileSchema,
} from './updateProfileSchema';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from '@/hooks/useTranslation';
import {
  convertNumber,
  processTitleCase,
} from '@/helpers/utils/functionality-utils';
import { DialogFooterTemplate } from '../Dialog/FooterTemplate';
import {
  useUpdateProfileInfoForSelfMutation,
  useUpdateProfilePicForSelfMutation,
} from '@/store/apis/user/userApiCalls';
import { useToastContext } from '../toast-context/useToastContext';
import type { NullableUpdateSelfRequestType } from '@/types/authresponse';
import { MultiSelect } from 'primereact/multiselect';
import {
  getAuthenticatedUserData,
  getDepartments,
} from '@/services/masters/user/userApiCalls';

export interface UpdateProfilePopupProps extends PopupProps {
  email?: string;
}

export const UpdateProfilePopup = ({
  open,
  setOpen,
}: UpdateProfilePopupProps) => {
  const dispatch = useAppDispatch();
  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation('users');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const isCustomer = !!useAppSelector(
    (state) => state.user.authUserData.data.customer?.c_id,
  );

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const { showError, showSuccess } = useToastContext();

  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
  } = useForm({
    defaultValues: updateProfileInitialState,
    resolver: zodResolver(updateProfileSchema),
  });

  const authUserData = useAppSelector((state) => state.user.authUserData.data);
  const email = authUserData?.email;
  const fName = authUserData?.first_name;
  const lName = authUserData?.last_name;
  const profilePic = authUserData?.pic;
  const dept = authUserData?.dept;
  const notifyTo = authUserData?.notified_to;
  const role = authUserData?.role;

  const departments = useAppSelector((state) => state.user.departments);

  const [preview, setPreview] = useState('');

  const [updateProPic, { isLoading: isUploadingProPic }] =
    useUpdateProfilePicForSelfMutation();
  const [updateProInfo, { isLoading: isUploadingProInfo }] =
    useUpdateProfileInfoForSelfMutation();

  useEffect(() => {
    dispatch(getDepartments());
  }, []);

  useEffect(() => {
    if (open) {
      if (fName) setValue('fname', fName);
      if (lName) setValue('lname', lName);
      if (notifyTo && convertNumber(notifyTo?.length) > 0)
        setValue(
          'notifyTo',
          notifyTo.map((value) => processTitleCase(value)),
        );
    }
  }, [fName, lName, notifyTo, open]);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);

    setImageSrc(imageUrl);
  };

  const getCroppedImg = async (imageSrc: string, crop: Area) => {
    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => (image.onload = resolve));

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx?.drawImage(
      image,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height,
    );

    return new Promise<Blob>((resolve) => {
      canvas.toBlob((blob) => resolve(blob!), 'image/jpeg');
    });
  };

  const handleCropUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

    const file = new File([croppedBlob], 'profile.jpg', {
      type: 'image/jpeg',
    });

    // preview
    setPreview(URL.createObjectURL(file));

    const formData = new FormData();
    formData.append('file', file);

    updateProPic(formData).then((res) => {
      if (res.error && 'data' in res.error) {
        const msg: unknown = res.error.data;
        showError(msg as string);
      } else {
        if (res?.data?.message) showSuccess(res?.data?.message);
        dispatch(getAuthenticatedUserData());
      }
    });

    setImageSrc(null);
  };

  function onCancel() {
    reset();
    setOpen(false);
  }

  function onSubmit() {
    const [fname, lname, notifyTo] = watch(['fname', 'lname', 'notifyTo']);

    const payload: NullableUpdateSelfRequestType = {
      first_name: fname,
      last_name: lname,
      subscribed_to: notifyTo.map((value) => String(value)),
    };

    updateProInfo(payload).then((res) => {
      if (res.error && 'data' in res.error) {
        const msg: unknown = res.error.data;
        showError(msg as string);
      } else {
        if (res?.data?.message) showSuccess(res?.data?.message);
        dispatch(getAuthenticatedUserData());
        onCancel();
      }
    });
  }

  return (
    <Dialog
      header={t('manageMyProfile')}
      visible={open}
      onHide={() => setOpen(false)}
      blockScroll
      footer={
        <DialogFooterTemplate
          submitBtnRef={submitBtnRef}
          loading={isUploadingProInfo || isUploadingProPic}
          onCancel={onCancel}
        />
      }
    >
      <form
        onSubmit={handleSubmit(() => onSubmit())}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col items-center">
                  <div className="relative h-40 w-40">
                    <div className="h-full w-full overflow-hidden rounded-full">
                      {preview || profilePic ? (
                        <img
                          src={preview || profilePic}
                          alt="profile"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <TbUserCircle className="h-full w-full" />
                      )}
                    </div>
                  </div>
                  {/* Upload Link */}
                  <button
                    type="button"
                    onClick={() => fileRef?.current?.click()}
                    className="text-sm font-medium !text-[var(--blue-400)] hover:!text-[var(--blue-600)] hover:underline"
                  >
                    {t('UploadProfile')}
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileRef}
                    onChange={handleSelect}
                    className="hidden"
                  />
                </div>
                {imageSrc && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
                    <div className="relative h-[400px] w-[400px] bg-white">
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={true}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={(_, croppedPixels) => {
                          setCroppedAreaPixels(croppedPixels);
                        }}
                      />
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                        <Button
                          label="Cancel"
                          onClick={() => setImageSrc(null)}
                        />
                        <Button
                          label="Crop & Upload"
                          type="button"
                          onClick={handleCropUpload}
                        />
                      </div>
                      <div className="absolute bottom-16 left-4 right-4">
                        <input
                          type="range"
                          min={1}
                          max={3}
                          step={0.1}
                          value={zoom}
                          onChange={(e) => setZoom(Number(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="ml-8 flex flex-col gap-3">
                  <div className="grid grid-cols-12 gap-6">
                    <LabelWrapper
                      error={t(errors.fname?.message || '')}
                      label={t('fName')}
                      containerClassName="col-span-6"
                      icon="pi pi-user"
                      iconPos="left"
                    >
                      <InputText
                        {...register('fname')}
                        value={watch('fname')}
                        onChange={(e) => setValue('fname', e.target.value)}
                        placeholder={t('fName')}
                        className="w-full"
                        keyfilter="alpha"
                      />
                    </LabelWrapper>
                    <LabelWrapper
                      error={t(errors.lname?.message || '')}
                      label={t('lName')}
                      containerClassName="col-span-6"
                      icon="pi pi-user"
                    >
                      <InputText
                        {...register('lname')}
                        value={watch('lname')}
                        onChange={(e) => setValue('lname', e.target.value)}
                        placeholder={t('lName')}
                        className="w-full"
                        keyfilter="alpha"
                      />
                    </LabelWrapper>
                  </div>
                  <LabelWrapper label={t('email')} containerClassName="w-full">
                    <div className="flex items-center gap-3 text-xl font-medium">
                      <i className="pi pi-envelope"></i>
                      {email}
                    </div>
                  </LabelWrapper>
                </div>
              </div>
              <div className="my-3 w-[95%] self-center border-b border-surface-300" />
              <div className="mt-3 grid grid-cols-12 gap-6">
                {!isCustomer && (
                  <LabelWrapper
                    containerClassName="col-span-6"
                    label={t('division')}
                  >
                    <div className="flex items-center gap-3 text-xl font-medium">
                      <TbBuilding />
                      {dept}
                    </div>
                  </LabelWrapper>
                )}
                <LabelWrapper containerClassName="col-span-6" label={t('role')}>
                  <div className="flex items-center gap-3 text-xl font-medium">
                    <TbShieldCheck />
                    {processTitleCase(role)}
                  </div>
                </LabelWrapper>
              </div>
              {!isCustomer && (
                <div className="mt-3 flex w-full">
                  <Controller
                    control={control}
                    name="notifyTo"
                    render={({ field }) => (
                      <LabelWrapper
                        containerClassName="w-full"
                        label={t('dn')}
                        error={t(errors.notifyTo?.message || '')}
                        icon={<TbBuilding />}
                      >
                        <MultiSelect
                          {...field}
                          loading={departments.loading}
                          options={departments.data.map((d) => {
                            return processTitleCase(d.name);
                          })}
                          placeholder={t('selectDN')}
                          className="w-full pl-6"
                          selectAllLabel="Select All Desired Notifications"
                        />
                      </LabelWrapper>
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <button ref={submitBtnRef} type="submit" className="hidden" />
      </form>
    </Dialog>
  );
};
